import React, { useEffect, useRef, useMemo, useState } from 'react';
import { truncateParty, formatNumber } from '../../utils/formatters';
import { useTopValidators } from '../../hooks/useTopValidators';
import { useTopHolders } from '../../hooks/useTopHolders';
import { LoadingLogo } from '../LoadingLogo';

// Helper to calculate time ago
function getTimeAgo(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  return { diffMs, diffMins, diffHours, diffDays, date };
}

// Format time ago for display
function formatTimeAgo(dateString) {
  const timeAgo = getTimeAgo(dateString);
  if (!timeAgo) return '—';

  const { diffMins, diffHours, diffDays } = timeAgo;
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}

// Get liveness status - green for live, monochrome for rest
function getLivenessStatus(dateString) {
  const timeAgo = getTimeAgo(dateString);
  if (!timeAgo) return { status: 'unknown', dotColor: 'var(--text-disabled)', textColor: 'var(--text-disabled)', glow: false };

  const { diffMins, diffHours } = timeAgo;
  if (diffMins < 10) return { status: 'live', dotColor: 'var(--accent)', textColor: 'var(--text-secondary)', glow: true };
  if (diffMins < 60) return { status: 'active', dotColor: 'var(--text-primary)', textColor: 'var(--text-secondary)', glow: false };
  if (diffHours < 24) return { status: 'recent', dotColor: 'var(--text-tertiary)', textColor: 'var(--text-tertiary)', glow: false };
  return { status: 'stale', dotColor: 'var(--text-disabled)', textColor: 'var(--text-disabled)', glow: false };
}

export function ValidatorsTab({ data, loading, highlightParty }) {
  const { validators: topValidators, asOfRound, loading: rewardsLoading } = useTopValidators(1000);
  const { holders, totalHoldings, totalHolders, top10Percent, loading: holdingsLoading } = useTopHolders();
  const highlightRef = useRef(null);
  const [sortBy, setSortBy] = useState('holdings'); // 'holdings' or 'rewards'

  // Create holdings lookup by party ID
  const holdingsLookup = useMemo(() => {
    const lookup = {};
    for (const h of holders) {
      lookup[h.party] = h;
    }
    return lookup;
  }, [holders]);

  // Use top validators (with rewards) if available, otherwise fall back to license data
  const baseValidators = topValidators.length > 0 ? topValidators : data.validators;
  const showRewards = topValidators.length > 0;

  // Sort validators based on selected option
  const displayValidators = useMemo(() => {
    const sorted = [...baseValidators].sort((a, b) => {
      if (sortBy === 'holdings') {
        const aHolding = holdingsLookup[a.party]?.totalBalance || 0;
        const bHolding = holdingsLookup[b.party]?.totalBalance || 0;
        return bHolding - aHolding;
      } else {
        return (b.rewards || 0) - (a.rewards || 0);
      }
    });
    return sorted;
  }, [baseValidators, holdingsLookup, sortBy]);

  // Calculate liveness metrics
  const livenessMetrics = useMemo(() => {
    const now = Date.now();
    let live = 0;
    let active = 0;
    let recent = 0;
    let stale = 0;

    for (const v of displayValidators) {
      const lastActive = v.lastActiveAt;
      if (!lastActive) {
        stale++;
        continue;
      }

      const diffMs = now - new Date(lastActive).getTime();
      const diffMins = diffMs / 60000;
      const diffHours = diffMs / 3600000;

      if (diffMins < 10) live++;
      else if (diffMins < 60) active++;
      else if (diffHours < 24) recent++;
      else stale++;
    }

    return { live, active, recent, stale, total: displayValidators.length };
  }, [displayValidators]);

  // Scroll to highlighted row when component mounts or highlightParty changes
  useEffect(() => {
    if (highlightParty && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightParty, displayValidators]);

  return (
    <section style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Stats Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        borderBottom: '1px solid var(--border-default)',
      }}>
        {/* Stat: Validators */}
        <div style={{
          padding: '16px',
          borderRight: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Validators
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {displayValidators.length}
          </div>
        </div>

        {/* Stat: Total Holdings */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Total CC
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {holdingsLoading ? '...' : formatNumber(totalHoldings)}
          </div>
        </div>

        {/* Stat: Top 10 Concentration */}
        <div style={{
          padding: '16px',
          borderRight: '1px solid var(--border-subtle)',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Top 10 Hold
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
            {holdingsLoading ? '...' : `${top10Percent.toFixed(0)}%`}
          </div>
        </div>

        {/* Stat: Liveness Breakdown - compact */}
        <div style={{
          padding: '16px',
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
            Status
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--accent)',
                boxShadow: '0 0 6px rgba(0, 255, 136, 0.5)',
              }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {livenessMetrics.live}
              </span>
              <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>live</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--text-primary)' }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {livenessMetrics.active}
              </span>
              <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>active</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--text-tertiary)' }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {livenessMetrics.recent}
              </span>
              <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>recent</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--text-disabled)' }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {livenessMetrics.stale}
              </span>
              <span style={{ fontSize: '9px', color: 'var(--text-tertiary)' }}>stale</span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Header with Sortable Columns */}
      <div className="hide-mobile" style={{
        display: 'grid',
        gridTemplateColumns: '50px minmax(0, 1.5fr) minmax(0, 1fr) 130px 120px 90px',
        gap: '16px',
        padding: '12px 24px',
        background: 'var(--bg-subtle)',
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        <div>#</div>
        <div>Validator</div>
        <div>Contact</div>
        <div
          onClick={() => setSortBy('holdings')}
          style={{
            textAlign: 'right',
            cursor: 'pointer',
            color: sortBy === 'holdings' ? 'var(--text-primary)' : 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '4px',
            transition: 'color 0.15s',
          }}
        >
          Holdings
          {sortBy === 'holdings' && <span style={{ fontSize: '10px' }}>▼</span>}
        </div>
        <div
          onClick={() => setSortBy('rewards')}
          style={{
            textAlign: 'right',
            cursor: 'pointer',
            color: sortBy === 'rewards' ? 'var(--text-primary)' : 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '4px',
            transition: 'color 0.15s',
          }}
        >
          Rewards
          {sortBy === 'rewards' && <span style={{ fontSize: '10px' }}>▼</span>}
        </div>
        <div style={{ textAlign: 'right' }}>Status</div>
      </div>

      {/* Mobile header */}
      <div className="show-mobile-only" style={{
        display: 'flex',
        padding: '10px 12px',
        background: 'var(--bg-subtle)',
        fontSize: '9px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        alignItems: 'center',
      }}>
        <div style={{ width: '30px', flexShrink: 0 }}>#</div>
        <div style={{ flex: 1 }}>Validator</div>
        <div
          onClick={() => setSortBy(sortBy === 'holdings' ? 'rewards' : 'holdings')}
          style={{
            width: '70px',
            textAlign: 'right',
            flexShrink: 0,
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          {sortBy === 'holdings' ? 'Holdings ▼' : 'Rewards ▼'}
        </div>
        <div style={{ width: '50px', textAlign: 'right', flexShrink: 0 }}>Status</div>
      </div>

      {/* Table Body */}
      <div style={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        {displayValidators.length > 0 ? (
          displayValidators.map((v, i) => {
            const isHighlighted = highlightParty && v.party === highlightParty;
            const liveness = getLivenessStatus(v.lastActiveAt);
            const timeAgo = formatTimeAgo(v.lastActiveAt);
            const holding = holdingsLookup[v.party];

            return (
              <div key={v.party || i} ref={isHighlighted ? highlightRef : null}>
                {/* Desktop row */}
                <div
                  className="hide-mobile"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50px minmax(0, 1.5fr) minmax(0, 1fr) 130px 120px 90px',
                    gap: '16px',
                    padding: '14px 24px',
                    borderBottom: '1px solid var(--border-subtle)',
                    alignItems: 'center',
                    transition: 'background 0.15s ease',
                    background: isHighlighted ? 'var(--accent-muted)' : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                  onMouseLeave={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    fontSize: '13px',
                    color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: i < 3 ? '600' : '400',
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {v.name || 'Validator'}
                    </div>
                    <div style={{
                      color: 'var(--text-tertiary)',
                      fontSize: '10px',
                      fontFamily: 'var(--font-mono)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {truncateParty(v.party)}
                    </div>
                  </div>
                  <div style={{
                    color: 'var(--text-tertiary)',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {v.contact || v.contactPoint || '—'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {holding ? (
                      <div>
                        <div style={{
                          color: sortBy === 'holdings' ? 'var(--text-primary)' : 'var(--text-secondary)',
                          fontSize: '13px',
                          fontWeight: sortBy === 'holdings' ? '600' : '400',
                          fontFamily: 'var(--font-mono)',
                        }}>
                          {formatNumber(holding.totalBalance)} CC
                        </div>
                        {data?.networkStats?.ccPrice && (
                          <div style={{
                            color: 'var(--text-tertiary)',
                            fontSize: '10px',
                            fontFamily: 'var(--font-mono)',
                          }}>
                            ${formatNumber(holding.totalBalance * data.networkStats.ccPrice)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>—</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {showRewards && v.rewards ? (
                      <div style={{
                        color: sortBy === 'rewards' ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontSize: '13px',
                        fontWeight: sortBy === 'rewards' ? '600' : '400',
                        fontFamily: 'var(--font-mono)',
                      }}>
                        {formatNumber(v.rewards)} CC
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>—</span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    justifyContent: 'flex-end',
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: liveness.dotColor,
                      boxShadow: liveness.glow ? '0 0 6px rgba(0, 255, 136, 0.5)' : 'none',
                    }} />
                    <span style={{
                      fontSize: '11px',
                      color: liveness.textColor,
                      fontFamily: 'var(--font-mono)',
                    }}>{timeAgo}</span>
                  </div>
                </div>

                {/* Mobile row */}
                <div
                  className="show-mobile-only"
                  style={{
                    display: 'flex',
                    padding: '12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    alignItems: 'center',
                    background: isHighlighted ? 'var(--accent-muted)' : 'transparent',
                  }}
                >
                  <div style={{
                    width: '30px',
                    flexShrink: 0,
                    fontSize: '12px',
                    color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    fontFamily: 'var(--font-mono)',
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: 'var(--text-primary)',
                      fontSize: '12px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {v.name || 'Validator'}
                    </div>
                  </div>
                  <div style={{
                    width: '70px',
                    flexShrink: 0,
                    textAlign: 'right',
                  }}>
                    <div style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                      color: 'var(--text-primary)',
                      fontWeight: '600',
                    }}>
                      {sortBy === 'holdings'
                        ? (holding ? formatNumber(holding.totalBalance) : '—')
                        : (v.rewards ? formatNumber(v.rewards) : '—')
                      }
                    </div>
                    {sortBy === 'holdings' && holding && data?.networkStats?.ccPrice && (
                      <div style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '8px',
                        color: 'var(--text-tertiary)',
                      }}>
                        ${formatNumber(holding.totalBalance * data.networkStats.ccPrice)}
                      </div>
                    )}
                  </div>
                  <div style={{
                    width: '50px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    justifyContent: 'flex-end',
                  }}>
                    <span style={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      background: liveness.dotColor,
                    }} />
                    <span style={{
                      fontSize: '9px',
                      color: liveness.textColor,
                      fontFamily: 'var(--font-mono)',
                    }}>{timeAgo}</span>
                  </div>
                </div>
              </div>
            );
          })
        ) : loading || rewardsLoading ? (
          <LoadingLogo size={40} />
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            No validators found
          </div>
        )}
      </div>
    </section>
  );
}
